import React, { useCallback, useEffect, useRef, useState } from 'react';
import DragMatchQuiz from '../quiz/DragMatchQuiz';

const apiBase = () => process.env.REACT_APP_API_URL || '';
const mediaBase = () => (process.env.REACT_APP_API_URL || '').replace('/api/v1', '');

const QUIZ_TYPE_LABEL = {
  single_choice_text: 'Chọn 1 đáp án',
  single_choice_image: 'Chọn hình',
  listening_single_choice: 'Nghe + chọn đáp án',
  listening_matching: 'Nghe + nối cặp',
  listening_drag_zone: 'Nghe + kéo vào vùng',
  listening_fill_blank: 'Nghe + điền từ',
  fill_in_the_blank: 'Điền vào chỗ trống',
  true_false: 'Đúng/Sai',
  multiple_choice_text: 'Chọn nhiều đáp án',
};

const supportsInteractive = (quiz) => {
  const qt = quiz.quiz_type || '';
  const opts = quiz.options || [];
  const ctx = quiz.context || {};
  const hasPersons = ctx.context_image_url && (ctx.persons || []).length > 0;
  if (hasPersons && (qt === 'listening_drag_zone' || qt === 'listening_matching' || qt === 'matching' || qt === 'drag_and_drop')) return 'drag';
  if (
    opts.length > 0 &&
    ['listening_single_choice', 'single_choice_text', 'single_choice_image', 'true_false', 'multiple_choice_text', 'multiple_choice_image'].includes(qt)
  ) {
    return 'choice';
  }
  if (qt === 'listening_fill_blank' || qt === 'fill_in_the_blank' || qt === 'short_answer') return 'text';
  return null;
};

/**
 * Full-screen overlay: random up to 5 quizzes for exercise_type, submit each, then summary.
 */
const LibraryQuizSessionRunner = ({ open, onClose, exerciseType, accentColor = '#6BB5E3' }) => {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('loading'); // loading | run | summary
  const [round, setRound] = useState(null); // after submit: { is_correct, feedback } — shown below question
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]); // { quizId, isCorrect, skipped }
  const feedbackRef = useRef(null);

  const reset = useCallback(() => {
    setQuizzes([]);
    setIndex(0);
    setPhase('loading');
    setRound(null);
    setSubmitting(false);
    setResults([]);
    setLoadError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    reset();

    const token = localStorage.getItem('accessToken');
    if (!token) {
      setLoadError('login');
      setPhase('summary');
      setQuizzes([]);
      return;
    }

    const et = typeof exerciseType === 'string' ? exerciseType.trim() : '';
    if (!et) {
      setLoadError('fetch');
      setPhase('summary');
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setPhase('loading');
      try {
        const res = await fetch(
          `${apiBase()}/quizzes/random-session?exercise_type=${encodeURIComponent(et)}&limit=5`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = json.data || [];
        if (cancelled) return;
        setQuizzes(data);
        if (data.length === 0) {
          setLoadError('empty');
          setPhase('summary');
        } else {
          setPhase('run');
          setIndex(0);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError('fetch');
          setPhase('summary');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, exerciseType, reset]);

  useEffect(() => {
    if (round && feedbackRef.current) {
      feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [round]);

  const recordResult = (quizId, isCorrect, skipped) => {
    setResults((prev) => [...prev, { quizId, isCorrect, skipped }]);
  };

  const submitAnswer = async (quizId, userAnswer) => {
    const token = localStorage.getItem('accessToken');
    if (!token || round) return;
    setSubmitting(true);
    try {
      const et = typeof exerciseType === 'string' ? exerciseType.trim() : '';
      const q = et ? `?exercise_type=${encodeURIComponent(et)}` : '';
      const res = await fetch(`${apiBase()}/quizzes/${quizId}/submit${q}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_answer: String(userAnswer) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRound({
        is_correct: json.is_correct,
        feedback: json.feedback || null,
        skipped: false,
      });
      recordResult(quizId, json.is_correct, false);
    } catch {
      setRound({ is_correct: false, feedback: 'Không gửi được đáp án. Thử lại.', skipped: false });
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (index + 1 >= quizzes.length) {
      setPhase('summary');
      return;
    }
    setIndex((i) => i + 1);
    setRound(null);
  };

  const skipQuestion = () => {
    const q = quizzes[index];
    if (!q) return;
    recordResult(q.id, false, true);
    if (index + 1 >= quizzes.length) {
      setPhase('summary');
    } else {
      setIndex((i) => i + 1);
      setRound(null);
    }
  };

  if (!open) return null;

  const overlayBase = {
    position: 'fixed',
    inset: 0,
    zIndex: 100,
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  };

  const panel = {
    width: '100%',
    maxWidth: 640,
    maxHeight: '90vh',
    overflow: 'auto',
    background: 'white',
    borderRadius: 20,
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
    padding: 24,
  };

  if (phase === 'loading' && loading) {
    return (
      <div style={overlayBase}>
        <div style={{ ...panel, textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
          Đang chọn quiz ngẫu nhiên…
        </div>
      </div>
    );
  }

  if (phase === 'summary') {
    const attempted = results.filter((r) => !r.skipped);
    const correct = results.filter((r) => !r.skipped && r.isCorrect).length;
    const skipped = results.filter((r) => r.skipped).length;
    const total = quizzes.length;

    return (
      <div style={overlayBase}>
        <div style={panel}>
          <h2 className="font-headline font-black text-xl text-hp-on-surface" style={{ margin: '0 0 8px' }}>
            Kết quả luyện tập
          </h2>
          {loadError === 'login' && (
            <p style={{ color: '#b45309', fontWeight: 600 }}>Đăng nhập để làm chuỗi quiz và lưu kết quả.</p>
          )}
          {loadError === 'empty' && (
            <p style={{ color: '#64748b' }}>Chưa có quiz nào cho phần này trong hệ thống.</p>
          )}
          {loadError === 'fetch' && (
            <p style={{ color: '#ef4444' }}>Không tải được quiz. Kiểm tra kết nối hoặc thử lại.</p>
          )}
          {!loadError && total > 0 && (
            <>
              <p style={{ fontSize: 16, color: '#334155', marginTop: 12, lineHeight: 1.6 }}>
                Bạn làm <strong>{attempted.length}</strong> / {total} câu
                {skipped > 0 && (
                  <> · <span style={{ color: '#94a3b8' }}>bỏ qua {skipped}</span></>
                )}
                .
              </p>
              {attempted.length === 0 ? (
                <p style={{ fontSize: 15, color: '#64748b', marginTop: 16 }}>
                  Chưa có câu nào được chấm (toàn bộ đã bỏ qua).
                </p>
              ) : (
                <>
                  <p style={{ fontSize: 28, fontWeight: 900, color: accentColor, marginTop: 16 }}>
                    Đúng {correct} / {attempted.length}
                  </p>
                  <p style={{ fontSize: 14, color: '#64748b', marginTop: 8 }}>
                    Tỷ lệ: {Math.round((correct / attempted.length) * 100)}%
                  </p>
                </>
              )}
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="font-headline font-black mt-8 w-full py-3 rounded-xl text-white"
            style={{ background: accentColor, border: 'none', cursor: 'pointer' }}
          >
            Đóng
          </button>
        </div>
      </div>
    );
  }

  const quiz = quizzes[index];
  const mode = quiz ? supportsInteractive(quiz) : null;
  const color = accentColor;

  return (
    <div style={overlayBase}>
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase' }}>
            Câu {index + 1} / {quizzes.length}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontWeight: 700 }}
          >
            ✕ Đóng
          </button>
        </div>

        {phase === 'run' && quiz && (
          <>
            <div style={{ marginBottom: 12 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '4px 10px',
                  borderRadius: 8,
                  background: `${color}22`,
                  color,
                  textTransform: 'uppercase',
                }}
              >
                {QUIZ_TYPE_LABEL[quiz.quiz_type] || quiz.quiz_type || 'Quiz'}
              </span>
            </div>

            {quiz.context?.context_text && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  fontSize: 13,
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {quiz.context.context_text}
              </div>
            )}

            {mode === 'drag' && (
              <DragMatchQuiz
                quiz={quiz}
                mediaBase={mediaBase()}
                hideRetry
                hideInlineFeedback
                onSubmitWithAnswer={({ userAnswer }) => {
                  if (!submitting && !round) submitAnswer(quiz.id, userAnswer);
                }}
              />
            )}

            {mode === 'choice' && (
              <QuizChoicePanel
                key={quiz.id}
                quiz={quiz}
                disabled={submitting || !!round}
                onPick={(id) => submitAnswer(quiz.id, id)}
                mediaBase={mediaBase()}
              />
            )}

            {mode === 'text' && (
              <QuizTextPanel
                key={quiz.id}
                disabled={submitting || !!round}
                onSubmit={(text) => submitAnswer(quiz.id, text)}
              />
            )}

            {!mode && (
              <div style={{ padding: 16, background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Dạng câu này chưa hỗ trợ trong luyện nhanh.</p>
                <button
                  type="button"
                  onClick={skipQuestion}
                  style={{
                    marginTop: 12,
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Bỏ qua câu này
                </button>
              </div>
            )}

            {mode && !round && (
              <button
                type="button"
                onClick={skipQuestion}
                style={{
                  marginTop: 14,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#94a3b8',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Bỏ qua câu này
              </button>
            )}

            {round && (
              <div
                ref={feedbackRef}
                style={{
                  marginTop: 20,
                  padding: 16,
                  borderRadius: 14,
                  background: round.is_correct ? '#f0fdf4' : '#fff1f2',
                  border: `2px solid ${round.is_correct ? '#86efac' : '#fecdd3'}`,
                }}
              >
                <p style={{ margin: 0, fontWeight: 900, fontSize: 16, color: round.is_correct ? '#166534' : '#9f1239' }}>
                  {round.is_correct ? 'Chính xác!' : 'Chưa đúng'}
                </p>
                {round.feedback && (
                  <p style={{ margin: '10px 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{round.feedback}</p>
                )}
                <button
                  type="button"
                  onClick={goNext}
                  className="font-headline font-black mt-4 w-full py-2.5 rounded-xl text-white"
                  style={{ background: color, border: 'none', cursor: 'pointer' }}
                >
                  {index + 1 >= quizzes.length ? 'Xem kết quả chung' : 'Câu tiếp theo'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function QuizChoicePanel({ quiz, onPick, disabled, mediaBase: base }) {
  const opts = quiz.options || [];
  return (
    <>
      {quiz.question?.question_text && (
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#1e293b', lineHeight: 1.5 }}>
          {quiz.question.question_text}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {opts.map((opt, i) => {
          const label = opt.id || String.fromCharCode(65 + i);
          return (
            <button
              key={opt.id ?? i}
              type="button"
              disabled={disabled}
              onClick={() => onPick(label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 12,
                border: '2px solid #e2e8f0',
                background: '#fff',
                cursor: disabled ? 'default' : 'pointer',
                textAlign: 'left',
                fontSize: 14,
                color: '#334155',
              }}
            >
              <span style={{ fontWeight: 800, color: '#6366f1' }}>{label}.</span>
              {opt.image_url ? (
                <img src={`${base}${opt.image_url}`} alt="" style={{ maxHeight: 56, borderRadius: 6 }} />
              ) : (
                <span>{opt.text}</span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

function QuizTextPanel({ onSubmit, disabled }) {
  const [val, setVal] = useState('');
  return (
    <div>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        disabled={disabled}
        placeholder="Nhập đáp án..."
        style={{
          width: '100%',
          padding: '12px 14px',
          borderRadius: 12,
          border: '2px solid #e2e8f0',
          fontSize: 15,
          marginBottom: 12,
          boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        disabled={disabled || !val.trim()}
        onClick={() => onSubmit(val.trim())}
        className="font-headline font-black w-full py-2.5 rounded-xl text-white"
        style={{
          background: '#6BB5E3',
          border: 'none',
          cursor: disabled || !val.trim() ? 'default' : 'pointer',
          opacity: disabled || !val.trim() ? 0.5 : 1,
        }}
      >
        Kiểm tra
      </button>
    </div>
  );
}

export default LibraryQuizSessionRunner;
